// The land damage pass: the GPU half of WS_DestructionManager's damage render target (`sDamage`
// of the landscape shaders, `DRS_FullSurfaceColor`, 8192x4096: a texel per world unit). The sim
// keeps the collision side (`sim::view::land(0)`); this target carries what the landscape and
// girder shaders read on top: R = the blast level (1 cut-out, 0.6 destroyed, 0.55 O.M.G., 0.45
// outline ring), G/B = the rim texture's red/alpha, A = 1 under a girder no blast has hit since.
//
// Port of `XomOglDrawThreadedDamageGeomSet` 0x119d680 with Root.System.WSDamage{V,F}:
// - `DrawStandardExplosion` 0x11a35a0, MODE_NORMAL: a quad of half side `radius` rotated by the
//   integer `angle`, the integer distance rule per pixel (`gl_FragCoord` truncated), the rim
//   (DamageRim4 of the theme) over its UV; blend MAX on RGB, REVERSE_SUBTRACT on alpha (ONE, ONE).
// - `DrawGirder` 0x11a4260, MODE_GIRDER: the girder's quad (`width = int(dimensions.x)`, integer
//   angle) writes (0, 1, 1, 1) with `BlendEquationSeparate(REVERSE_SUBTRACT, ADD)`,
//   `BlendFuncSeparate(ONE, ONE, ONE, ZERO)`: R kept, G and B (the rim) cleared, A = 1.
// - `DrawOMGExplosion` 0x11a3720: the occluder surface (`DRS_OMGOccluder`, R8 at the target's
//   size) cleared and every occluder drawn in MODE_OCCLUDER (1.0, no blending), then the cone in
//   MODE_OMG: R = (1 - occluder) × 0.55, A = 1 - occluder with `BlendEquationSeparate(MAX,
//   REVERSE_SUBTRACT)`, `BlendFunc(ONE, ONE)`. The sim sends the integral world vertices.
import * as THREE from 'three';
import { assetJson, assetUrl } from '../assets.ts';
import { DMG_BLAST, DMG_CLEAR, DMG_GIRDER, DMG_OMG, DMG_OMG_OCCLUDER, type DamageEntry } from './camera.ts';

const MODE_NORMAL = 1, MODE_OCCLUDER = 2, MODE_OMG = 3, MODE_GIRDER = 5;

const VERT = /* glsl */ `
uniform float radius;
uniform vec3 centerPosition;
uniform int angle;
uniform int mode;
uniform vec2 dimensions;
out vec2 vUv;
void main() {
  vUv = (position.xy + 1.0) * 0.5;
  float a = float(angle) * 0.0174532925199433;
  float s = sin(a), c = cos(a);
  vec2 p;
  if (mode == ${MODE_NORMAL}) {
    p = vec2(position.x * c - position.y * s, position.x * s + position.y * c) * radius + centerPosition.xy;
  } else if (mode == ${MODE_OCCLUDER} || mode == ${MODE_OMG}) {
    p = position.xy;
  } else {
    float width = float(int(dimensions.x));
    p = vec2(position.x * width * c - position.y * dimensions.y * s, position.x * width * s + position.y * dimensions.y * c) + centerPosition.xy;
  }
  gl_Position = projectionMatrix * viewMatrix * vec4(p, 0.0, 1.0);
}`;

const FRAG = /* glsl */ `
precision highp float;
precision highp int;
uniform float radius;
uniform vec2 screenSpaceCentroid;
uniform float cutOut;
uniform sampler2D sRim;
uniform sampler2D sOccluderSurface;
uniform int mode;
in vec2 vUv;
out vec4 outColour;
void main() {
  ivec2 iPos = ivec2(gl_FragCoord.xy);
  if (mode == ${MODE_GIRDER}) { outColour = vec4(0.0, 1.0, 1.0, 1.0); return; }
  if (mode == ${MODE_OCCLUDER}) { outColour = vec4(1.0); return; }
  if (mode == ${MODE_OMG}) {
    float occ = texelFetch(sOccluderSurface, iPos, 0).r;
    outColour = vec4((1.0 - occ) * (0.5 + 0.05), 0.0, 0.0, 1.0 - occ);
    return;
  }
  ivec2 iCenter = ivec2(screenSpaceCentroid);
  int iRadius = int(radius);
  int iRadiusSquared = iRadius * iRadius;
  int iRadiusHalf = iRadius / 2;
  int iRadiusHalfSquared = iRadiusHalf * iRadiusHalf;
  if (iRadius % 2 != 0) iRadiusHalfSquared = (iRadiusHalfSquared + (iRadiusHalf + 1) * (iRadiusHalf + 1)) / 2;
  int iRadius0625 = iRadiusHalf + iRadius / 8;
  ivec2 dd = iCenter - iPos;
  int d2 = dd.x * dd.x + dd.y * dd.y;
  float d = 0.0;
  if (d2 <= iRadiusSquared) {
    float radiusCutOut = (1.0 - clamp(cutOut, 0.0, 1.0)) * radius;
    if (d2 < int(radiusCutOut * radiusCutOut)) d = 1.0;
    else if (d2 <= iRadiusHalfSquared) d = 0.6;
    else if (d2 <= iRadius0625 * iRadius0625) d = 0.45;
  }
  vec4 rim = texture(sRim, vUv);
  outColour = vec4(d, rim.r, rim.a, d >= 0.49 ? 1.0 : 0.0);
}`;

/** `DRS_FullSurfaceColor`: the world, a texel per unit. */
export const DAMAGE_W = 8192, DAMAGE_H = 4096;

type XomGraph = { graph: { objects: Record<string, { class: string; png?: string | null; ktx2?: string | null }> } };

export class DamageLayer {
  readonly target = new THREE.WebGLRenderTarget(DAMAGE_W, DAMAGE_H, {
    depthBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false,
  });
  /** `DRS_OMGOccluder` (created with the first O.M.G. cone). */
  private occluder: THREE.WebGLRenderTarget | null = null;
  /** Whether the target holds the damage since the land's first clear (a client that joined
   * mid-match has no history: the landscape falls back to the collision bitmap). */
  history = false;
  private scene = new THREE.Scene();
  private camera = new THREE.OrthographicCamera(-DAMAGE_W / 2, DAMAGE_W / 2, DAMAGE_H / 2, -DAMAGE_H / 2, -1, 1);
  private material: THREE.ShaderMaterial;
  private quad: THREE.Mesh;
  private quadGeometry: THREE.BufferGeometry;
  private pending: DamageEntry[] = [];
  private occluders: number[][] = [];
  private cleared = false;

  constructor() {
    this.material = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        radius: { value: 0 }, centerPosition: { value: new THREE.Vector3() }, angle: { value: 0 }, mode: { value: MODE_NORMAL },
        dimensions: { value: new THREE.Vector2() }, screenSpaceCentroid: { value: new THREE.Vector2() }, cutOut: { value: 1 },
        sRim: { value: null }, sOccluderSurface: { value: null },
      },
      depthTest: false, depthWrite: false, side: THREE.DoubleSide,
    });
    this.quadGeometry = new THREE.BufferGeometry();
    this.quadGeometry.setAttribute('position', new THREE.Float32BufferAttribute([1, -1, 0, -1, -1, 0, -1, 1, 0, 1, 1, 0, 1, -1, 0, -1, 1, 0], 3));
    this.quad = new THREE.Mesh(this.quadGeometry, this.material);
    this.quad.frustumCulled = false;
    this.scene.add(this.quad);
  }

  /** The theme's rim texture (`Bundles/<theme>/DamageRim4`, Rim4.tga). Blasts queue until it
   * has loaded. */
  async load(theme: string | null): Promise<void> {
    const desc = await assetJson<XomGraph>(`xom/DataLinux/Bundles/${theme ?? 'China'}/DamageRim4.json`);
    const img = Object.values(desc.graph.objects).find(o => o.class === 'XImage' && o.png);
    if (!img?.png) throw new Error(`DamageRim4 of ${theme}: no image`);
    const tex = await new THREE.TextureLoader().loadAsync(assetUrl(img.png));
    tex.flipY = false;
    tex.generateMipmaps = false;
    tex.minFilter = THREE.LinearFilter;
    tex.needsUpdate = true;
    this.material.uniforms.sRim.value = tex;
  }

  queue(entries: DamageEntry[]): void {
    this.pending.push(...entries);
  }

  /** Sets the material's blend state (`glBlendEquationSeparate`/`glBlendFuncSeparate`). */
  private blend(eq: THREE.BlendingEquation, eqA: THREE.BlendingEquation, src: THREE.BlendingSrcFactor, dst: THREE.BlendingDstFactor, srcA: THREE.BlendingSrcFactor, dstA: THREE.BlendingDstFactor): void {
    const m = this.material;
    m.blending = THREE.CustomBlending;
    m.blendEquation = eq;
    m.blendEquationAlpha = eqA;
    m.blendSrc = src;
    m.blendDst = dst;
    m.blendSrcAlpha = srcA;
    m.blendDstAlpha = dstA;
  }

  /** Draws `verts` (world x, y pairs, triangles) with the current material state. */
  private triangles(gl: THREE.WebGLRenderer, verts: number[]): void {
    const g = new THREE.BufferGeometry();
    const p: number[] = [];
    for (let i = 0; i + 1 < verts.length; i += 2) p.push(verts[i], verts[i + 1], 0);
    g.setAttribute('position', new THREE.Float32BufferAttribute(p, 3));
    this.quad.geometry = g;
    gl.render(this.scene, this.camera);
    this.quad.geometry = this.quadGeometry;
    g.dispose();
  }

  /** `DrawOMGExplosion` 0x11a3720: the queued occluders into the occluder surface, then the
   * cone `cone` (three world vertices) against it. */
  private omg(gl: THREE.WebGLRenderer, cone: number[]): void {
    this.occluder ??= new THREE.WebGLRenderTarget(DAMAGE_W, DAMAGE_H, {
      depthBuffer: false, format: THREE.RedFormat, type: THREE.UnsignedByteType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false,
    });
    const u = this.material.uniforms;
    gl.setRenderTarget(this.occluder);
    gl.clear(true, false, false);
    u.mode.value = MODE_OCCLUDER;
    this.material.blending = THREE.NoBlending;
    for (const o of this.occluders) this.triangles(gl, o);
    this.occluders = [];
    gl.setRenderTarget(this.target);
    u.mode.value = MODE_OMG;
    u.sOccluderSurface.value = this.occluder.texture;
    this.blend(THREE.MaxEquation, THREE.ReverseSubtractEquation, THREE.OneFactor, THREE.OneFactor, THREE.OneFactor, THREE.OneFactor);
    this.triangles(gl, cone.slice(0, 6));
  }

  /** Draws the queued entries (before the frame's land is drawn). */
  flush(gl: THREE.WebGLRenderer): void {
    if (this.cleared && !this.pending.length) return;
    if (!this.material.uniforms.sRim.value) return;
    const prevTarget = gl.getRenderTarget();
    const prevAuto = gl.autoClear;
    const prevColour = gl.getClearColor(new THREE.Color());
    const prevAlpha = gl.getClearAlpha();
    gl.autoClear = false;
    gl.setRenderTarget(this.target);
    gl.setClearColor(0x000000, 0);
    if (!this.cleared) {
      gl.clear(true, false, false);
      this.cleared = true;
    }
    const u = this.material.uniforms;
    for (const e of this.pending) {
      if (e.kind === DMG_CLEAR) {
        gl.setRenderTarget(this.target);
        gl.clear(true, false, false);
        this.history = true;
      } else if (e.kind === DMG_BLAST) {
        const [x, y, z, radius, angle, cutOut, , sceneW, sceneH] = e.args;
        u.mode.value = MODE_NORMAL;
        u.radius.value = radius;
        u.centerPosition.value.set(x, y, z);
        u.angle.value = Math.trunc(angle);
        u.cutOut.value = cutOut;
        u.screenSpaceCentroid.value.set(Math.fround(sceneW * 0.5 + x), Math.fround(sceneH * 0.5 + y));
        this.blend(THREE.MaxEquation, THREE.ReverseSubtractEquation, THREE.OneFactor, THREE.OneFactor, THREE.OneFactor, THREE.OneFactor);
        gl.render(this.scene, this.camera);
      } else if (e.kind === DMG_GIRDER) {
        const [x, y, z, w, h, angle] = e.args;
        u.mode.value = MODE_GIRDER;
        u.centerPosition.value.set(x, y, z);
        u.dimensions.value.set(w, h);
        u.angle.value = Math.trunc(angle);
        this.blend(THREE.ReverseSubtractEquation, THREE.AddEquation, THREE.OneFactor, THREE.OneFactor, THREE.OneFactor, THREE.ZeroFactor);
        gl.render(this.scene, this.camera);
      } else if (e.kind === DMG_OMG_OCCLUDER) {
        // Trailing zero arguments are not sent.
        this.occluders.push(Array.from({ length: 12 }, (_, k) => e.args[k] ?? 0));
      } else if (e.kind === DMG_OMG) {
        this.omg(gl, e.args);
      }
    }
    this.pending = [];
    gl.setRenderTarget(prevTarget);
    gl.setClearColor(prevColour, prevAlpha);
    gl.autoClear = prevAuto;
  }
}
